import React, { useState, useEffect } from 'react';
import { View, Text, Pressable } from 'react-native';
import { useLazyQuery, useMutation } from '@apollo/client';
import styled from 'styled-components/native';
import htmlStyled from 'styled-components';
import DataTable from '@preflighttech/preflight-tables';
import { DateTime } from 'luxon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrashAlt } from '@fortawesome/free-solid-svg-icons';

import DataState from 'app/src/components/DataState';
import ExpenseEditModal from 'app/src/components/Accounting/Expenses/EditModal';
import { Button } from 'app/src/elements/buttons';
import minimalTable from 'app/src/elements/minimalTable';
import {
  downloadReport,
  DownloadButton
} from 'app/src/elements/ReportDownload';
import { elRed } from 'app/src/styles/colors';
import { dateShort } from 'app/src/utils/timeFormatter';

import { currency } from './Process';
import SaleAssetField from './Process/SaleAssetField';
import {
  ACTIVE_ACH_BATCH, REMOVE_FROM_ACTIVE_BATCH, FINALIZE_ACH_BATCH
} from './queries';

const TABLE_STYLES = {
  table: {
    backgroundColor: 'white',
  },
  header: {
    container: {
      textAlign: 'left',
      alignContext: null,
    },
  },
  header: {
    label: { fontSize: 14 }
  },
  data: {
    cell: {
      fontFamily: 'sans-serif',
      fontSize: 14,
      paddingVertical: 0,
      paddingHorizontal: 0,
      height: 30
    }
  },
};

const Value = styled(Text)`
  margin-left: 6px;
  margin-right: 6px;

  ${props => props.subtotal ? 'font-weight: 600;': ''}
`;

const AchBatchModal = ({achBatch, editable, noExcel, noPdf}) => {
  const [edit, setEdit] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState([]);
  const [getAchBatch, { loading, data, refetch }] = useLazyQuery(
    ACTIVE_ACH_BATCH, { fetchPolicy: 'no-cache' }
  );

  const [removeFromBatch] = useMutation(REMOVE_FROM_ACTIVE_BATCH, {
    onCompleted: refetch
  });

  const [finalizeBatch] = useMutation(FINALIZE_ACH_BATCH, {
    onCompleted: refetch
  });

  useEffect(() => {
    if(!achBatch) {
      getAchBatch();
    }
  }, [])

  useEffect(() => {
    if(data || achBatch) {
      const updatedEntries = [];
      let updatedTotal = 0.0;

      (achBatch || data.achBatch).groupedEntries.forEach(props => {
        const { entries, ...group } = props;

        let subtotal = 0.0;

        entries.forEach(entry => {
          updatedEntries.push(entry);
          subtotal = subtotal + parseFloat(entry.txn.amount);
        });

        updatedTotal += subtotal;

        updatedEntries.push({
          ...group,
          amount: subtotal,
          __typename: 'subtotal',
        });
      });

      setEntries(updatedEntries);
      setTotal(updatedTotal);
    }
  }, [data]);

  if(loading) {
    return <DataState.Loading />;
  }

  const columns = [
    {
      key: 'edit',
      label: '',
      sort: 'prevent',
      content: ({ entry }) => {
        return (
          (editable && 'subtotal' !== entry.__typename) ?
            <View style={{display: 'flex', flexDirection: 'row', gap: '5px'}}>
              <Pressable
                onPress={() => {
                  if (entry.saleAsset) {
                    setEdit(edit?.id === entry.id ? null : entry);
                    refetch();
                  } else {
                    setShowEditModal(entry.id);
                  }
                }}
              >
                <FontAwesomeIcon
                  icon={faEdit}
                  title="Edit"
                  color={elRed}
                />
              </Pressable>
              <Pressable
                onPress={() => {
                  const txnLabel = entry.saleAsset ?
                    entry.saleAsset.lotNumber :
                    `${currency(entry.txn.amount)} Expense`;

                  const confirmation = `Remove ${txnLabel} from Current Batch?`;

                  if (window.confirm(confirmation)) {
                    removeFromBatch({ variables: { txnId: entry.txn.id } });
                  }
                }}
              >
                <FontAwesomeIcon
                  icon={faTrashAlt}
                  title="Remove from Batch"
                  color={elRed}
                />
              </Pressable>
            </View> :
            null
        )
      }
    },
    {
      key: 'entityName',
      label: 'Seller/Vendor',
      sort: 'prevent',
      content: ({ value, entry }) => (
        <Value subtotal={ 'subtotal' === entry.__typename }>{value}</Value>
      ),
    },
    {
      key: 'lotNumber',
      label: 'Lot #',
      sort: 'prevent',
      value: ({ entry }) => entry.saleAsset?.lotNumber,
      content: ({ value }) => (
        <Value style={{ whiteSpace: 'nowrap' }}>{value}</Value>
      ),
    },
    {
      key: 'stockNumber',
      label: 'Stock #',
      sort: 'prevent',
      value: ({ entry }) => entry.saleAsset?.asset.stockNumber,
      content: ({ value }) => <Value>{value}</Value>,
    },
    {
      key: 'invoiceNumber',
      label: 'Invoice #',
      sort: 'prevent',
      value: ({ entry }) => entry.saleAsset?.invoiceNumber,
      content: ({ value }) => <Value>{value}</Value>,
    },
    {
      key: 'year',
      label: 'Year',
      sort: 'prevent',
      value: ({ entry }) => entry.saleAsset?.asset.year,
      content: ({ value}) => <Value>{value}</Value>,
    },
    {
      key: 'make',
      label: 'Make',
      cellStyle: { maxWidth: 160 },
      sort: 'prevent',
      value: ({ entry }) => entry.saleAsset?.asset.make,
      content: ({ value}) => <Value>{value}</Value>,
    },
    {
      key: 'model',
      label: 'Model',
      sort: 'prevent',
      value: ({ entry }) => entry.saleAsset?.asset.model,
      content: ({ value}) => <Value>{value}</Value>,
    },
    {
      key: 'vin',
      label: 'VIN',
      sort: 'prevent',
      value: ({ entry }) => (
        'subtotal' === entry.__typename ?
        `${entry.entityType} Total` :
          entry.saleAsset?.asset.vin
      ),
      content: ({ value, entry }) => (
        <Value subtotal={ 'subtotal' === entry.__typename }>{value}</Value>
      ),
    },
    {
      key: 'amount',
      label: 'Amount',
      sort: 'prevent',
      headerStyle: { maxWidth: 50 },
      cellStyle: { textAlign: 'right' },
      content: ({ entry }) => {
        return (
          <>
            {editable && edit === entry ? (
              <SaleAssetField
                saleAsset={entry.saleAsset}
                field="sellerAchInstRec"
                style={{width: 60}}
              />
            ) : (
              <Value subtotal={ 'subtotal' === entry.__typename }>
                {currency(entry.amount || entry.txn?.amount)}
              </Value>
            )}
          </>
        )
      }
    },
    {
      key: 'bankDate',
      label: 'Date of ACH',
      sort: 'prevent',
      headerStyle: { maxWidth: 60 },
      content: ({ entry, value }) => {
        if(editable && edit === entry) {
          return (
            <SaleAssetField
              saleAsset={entry.saleAsset}
              field="sellerAchOn"
              isDate
            />
          )
        }else if (entry.txn) {
          return (
            <Value>{dateShort(entry.txn.bankDate)}</Value>
          )
        } else {
          return null;
        }
      }
    },
  ]

  const handleDownload = downloadProps => {
    downloadReport({
      ...downloadProps,
      path: `download-ach-batch`,
      params: { id: achBatch.id },
    });
  };

  const handleFinalize = () => {
    if (window.confirm(`Send ACH Batch?`)) {
      finalizeBatch();
    }
  };

  const Buttons = (
    <View
      style={{
        marginLeft: 160,
        flexDirection: 'row',
        gap: 10,
        alignItems: 'center'
      }}
    >
      <Text style={{ fontSize: 20 }}>
        Total: {currency(total)}
      </Text>

      <DownloadButton
        handleDownload={handleDownload}
        noExcel={noExcel}
        noPdf={noPdf}
      />

      {data?.achBatch.groupedEntries.length > 0 && !data.sentAt && (
        <Button
          style={{ marginTop: 0, marginBottom: 0 }}
          label="Finalize Batch"
          onPress={() => handleFinalize()}
        />
      )}
    </View>
  );

  return (
    <View style={{ marginBottom: 20 }}>
      <DataTable.Simple
        data={entries}
        columns={columns}
        pageLength="All"
        buttons={Buttons}
        styles={TABLE_STYLES}
        htmlTable
        // memoizeRows
      />

      <Text style={{
        fontSize: 18, textAlign: 'right', marginRight: 80, marginTop: 10
      }}>
        Batch Total: {currency(total)}
      </Text>

      {showEditModal && (
        <ExpenseEditModal
          id={showEditModal}
          setShowModal={setShowEditModal}
          refetch={refetch}
        />
      )}
    </View>
  )
}

export default AchBatchModal;
